import { Routes } from '@angular/router';
import { BaseLayoutComponent } from '@layouts/base/base.layout.component';
import { EnsureUserIsAuthenticated } from '@guard/ensure-user-authenticated';
import { ensureOnlineGuard } from '@guard/ensure-online.guard';
import { ensureScanAccessGuard } from '@guard/ensure-scan-access.guard';
import { sessionOpenGuard } from '@guard/session-open.guard';
import { hasInstanceGuard } from '@guard/has-instance.guard';
import { ensureTenantMatchGuard } from '@guard/ensure-tenant-match.guard';
import { AppRoutes } from '@helpers/app-routes';
import { chooseOrderTypeResolver } from '@pages/choose-order-type-page/choose-order-type.resolver';
import { chooseMealPlanResolver } from '@pages/choose-meal-plan-page/choose-meal-plan.resolver';
import { orderMenuResolver } from '@pages/order-page/order-menu.resolver';

export const routes: Routes = [
  { path: '', redirectTo: AppRoutes.login, pathMatch: 'full' },
  {
    path: '',
    component: BaseLayoutComponent,
    children: [
      {
        path: AppRoutes.login,
        loadComponent: () =>
          import('@pages/login-page/login-page.component').then((m) => m.LoginPageComponent),
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: false,
            showDeliveryButton: false,
            showPreferencesButton: true,
            showSettingsButton: false,
          },
        },
      },
      {
        path: AppRoutes.resetPasswordRequest,
        loadComponent: () =>
          import('@pages/reset-password-request-page/reset-password-request-page.component').then(
            (m) => m.ResetPasswordRequestPageComponent,
          ),
        canActivate: [ensureOnlineGuard],
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: false,
            showDeliveryButton: false,
            showPreferencesButton: true,
            showSettingsButton: false,
          },
        },
      },
      {
        path: `${AppRoutes.resetPassword}/:token`,
        loadComponent: () =>
          import('@pages/reset-password-page/reset-password-page.component').then(
            (m) => m.ResetPasswordPageComponent,
          ),
        canActivate: [ensureOnlineGuard],
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: false,
            showDeliveryButton: false,
            showPreferencesButton: true,
            showSettingsButton: false,
          },
        },
      },
    ],
  },
  {
    path: '',
    canActivate: [EnsureUserIsAuthenticated, ensureTenantMatchGuard],
    component: BaseLayoutComponent,
    children: [
      {
        path: AppRoutes.openSession,
        loadComponent: () =>
          import('@pages/open-session-page/open-session-page.component').then(
            (m) => m.OpenSessionPageComponent,
          ),
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: true,
            showDeliveryButton: true,
            showPreferencesButton: true,
            showSettingsButton: true,
          },
        },
      },
      {
        path: AppRoutes.settings,
        loadComponent: () =>
          import('@pages/settings-page/settings-page.component').then(
            (m) => m.SettingsPageComponent,
          ),
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: true,
            showDeliveryButton: true,
            showPreferencesButton: true,
            showSettingsButton: true,
          },
        },
      },
      {
        path: AppRoutes.sessionList,
        loadComponent: () =>
          import('@pages/session-list-page/session-list-page.component').then(
            (m) => m.SessionListPageComponent,
          ),
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: true,
            showDeliveryButton: true,
            showPreferencesButton: true,
            showSettingsButton: true,
          },
        },
      },
      {
        path: AppRoutes.chooseInstance,
        loadComponent: () =>
          import('@pages/choose-instance-page/choose-instance-page.component').then(
            (m) => m.ChooseInstancePageComponent,
          ),
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: true,
            showDeliveryButton: true,
            showPreferencesButton: true,
            showSettingsButton: true,
          },
        },
      },
      {
        path: AppRoutes.adminSettings,
        loadComponent: () =>
          import('@pages/admin-settings-page/admin-settings-page.component').then(
            (m) => m.AdminSettingsPageComponent,
          ),
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: true,
            showDeliveryButton: true,
            showPreferencesButton: true,
            showSettingsButton: true,
          },
        },
      },
    ],
  },
  {
    path: '',
    canActivate: [
      ensureScanAccessGuard,
      ensureTenantMatchGuard,
      hasInstanceGuard,
      sessionOpenGuard,
    ],
    component: BaseLayoutComponent,
    children: [
      {
        path: AppRoutes.scanPage,
        loadComponent: () =>
          import('@pages/scan-page/scan-page.component').then((m) => m.ScanPageComponent),
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: true,
            showDeliveryButton: true,
            showPreferencesButton: true,
            showSettingsButton: true,
          },
        },
      },
    ],
  },
  {
    path: '',
    canActivate: [
      EnsureUserIsAuthenticated,
      ensureTenantMatchGuard,
      hasInstanceGuard,
      sessionOpenGuard,
    ],
    component: BaseLayoutComponent,
    children: [
      {
        path: AppRoutes.closeSession,
        loadComponent: () =>
          import('@pages/close-session-page/close-session-page.component').then(
            (m) => m.CloseSessionPageComponent,
          ),
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: true,
            showDeliveryButton: true,
            showPreferencesButton: true,
            showSettingsButton: true,
          },
        },
      },
      {
        path: AppRoutes.orderList,
        loadComponent: () =>
          import('@pages/order-list-page/order-list-page.component').then(
            (m) => m.OrderListPageComponent,
          ),
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: true,
            showDeliveryButton: true,
            showPreferencesButton: true,
            showSettingsButton: true,
          },
        },
      },
      {
        path: `${AppRoutes.orderDetail}/:id`,
        loadComponent: () =>
          import('@pages/order-detail-page/order-detail-page.component').then(
            (m) => m.OrderDetailPageComponent,
          ),
        data: {
          header: {
            showLocation: true,
            showCashRegister: true,
            showStatus: true,
            showUserMenu: true,
            showDeliveryButton: true,
            showPreferencesButton: true,
            showSettingsButton: true,
          },
        },
      },
      {
        path: '',
        loadComponent: () =>
          import('@layouts/order-header/order-header.component').then(
            (m) => m.OrderHeaderComponent,
          ),
        children: [
          {
            path: AppRoutes.chooseOrderType,
            loadComponent: () =>
              import('@pages/choose-order-type-page/choose-order-type-page.component').then(
                (m) => m.ChooseOrderTypePageComponent,
              ),
            resolve: { modes: chooseOrderTypeResolver },
            data: {
              header: {
                showLocation: true,
                showCashRegister: true,
                showStatus: true,
                showUserMenu: true,
                showDeliveryButton: true,
                showPreferencesButton: true,
                showSettingsButton: true,
              },
            },
          },
          {
            path: AppRoutes.chooseMealPlan,
            loadComponent: () =>
              import('@pages/choose-meal-plan-page/choose-meal-plan-page.component').then(
                (m) => m.ChooseMealPlanPageComponent,
              ),
            resolve: { mealPlans: chooseMealPlanResolver },
            data: {
              header: {
                showLocation: true,
                showCashRegister: true,
                showStatus: true,
                showUserMenu: true,
                showDeliveryButton: true,
                showPreferencesButton: true,
                showSettingsButton: true,
              },
            },
          },
          {
            path: AppRoutes.order,
            loadComponent: () =>
              import('@pages/order-page/order-page.component').then((m) => m.OrderPageComponent),
            resolve: { menu: orderMenuResolver },
            data: {
              header: {
                showLocation: true,
                showCashRegister: true,
                showStatus: true,
                showUserMenu: true,
                showDeliveryButton: true,
                showPreferencesButton: true,
                showSettingsButton: true,
              },
            },
          },
          {
            path: AppRoutes.orderSummary,
            loadComponent: () =>
              import('@pages/order-summary-page/order-summary-page.component').then(
                (m) => m.OrderSummaryPageComponent,
              ),
            data: {
              header: {
                showLocation: true,
                showCashRegister: true,
                showStatus: true,
                showUserMenu: true,
                showDeliveryButton: true,
                showPreferencesButton: true,
                showSettingsButton: true,
              },
            },
          },
        ],
      },
    ],
  },
];
